import React, { useState, useEffect } from 'react';
import { Table, Row, Col, Button, Modal, Form, Card, Alert, Container, FormControl, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPlus, faEye } from '@fortawesome/free-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMeals, addMeal, deleteMeal, updateMeal } from '../redux/slice/mealSlice';
import '../assets/MealManagement.css';
import LoadingComponent from '../components/LoadingComponent';
import Notifications from '../components/Notifications';

const MealManagement = () => {
  const [showModal, setShowModal] = useState(false);
  const [editMeal, setEditMeal] = useState(null);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertVariant, setAlertVariant] = useState('info');
  const [mealCount, setMealCount] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState({ action: '', user: null });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
  });

  const [showToast, setShowToast] = useState(false);
  const [selectedAllergens, setSelectedAllergens] = useState([]);

  const dispatch = useDispatch();
  const { meals, status, error } = useSelector(state => state.mealDetails);

  useEffect(() => {

    const fetchData = async () => {
      try {
        await dispatch(fetchMeals());
      } catch (error) {

      }
    };

    fetchData();
  }, [dispatch]);

  const sortedMeals = [...meals].sort((a, b) => {
    return a.name.localeCompare(b.name, 'el', { sensitivity: 'base' });
  });

  const normalizeText = (text) => {
    return text
      .normalize('NFD') // Αποδομεί τους τόνους από τα γράμματα
      .replace(/[\u0300-\u036f]/g, '') // Αφαιρεί τους τόνους
      .toUpperCase(); // Μετατρέπει σε πεζά
  };

  const allergens = [...new Set(
    sortedMeals
      .map(meal => meal.description)
      .filter(description => description.trim() !== '' && description !== '-') // Αποφυγή κενών ή παυλών
      .flatMap(description =>
        description.split(',').map(a => normalizeText(a.trim())) // Κανονικοποίηση κειμένου
      )
      .filter(a => a !== '') // Αποφυγή κενών συμβολοσειρών
  )];


  // Χειρισμός αλλαγής για τα αλλεργιογόνα
  const handleAllergenChange = (e) => {
    const allergen = e.target.value;
    if (e.target.checked) {
      setSelectedAllergens(prev => [...prev, allergen]);
    } else {
      setSelectedAllergens(prev => prev.filter(a => a !== allergen));
    }
  };

  // Χειρισμός αλλαγής για τα γεύματα χωρίς αλλεργιογόνα
  const handleNoAllergensChange = (e) => {
    if (e.target.checked) {
      setSelectedAllergens(prev => [...prev, '-']);
    } else {
      setSelectedAllergens(prev => prev.filter(a => a !== '-'));
    }
  };

  const handleShowConfirm = (action, user, e) => {
    setShowModal(false);
    setConfirmAction({ action, user });
    setShowConfirmModal(true);
  };

  const handleConfirmClose = () => {
    setShowConfirmModal(false);
  };

  const handleConfirmAction = async () => {
    const { action, user } = confirmAction;
    try {
      if (action === 'delete') {
        await handleDelete(user.id);
      } else if (action === 'edit' || action === 'add') {
        handleSave(); // Κλήση της συνάρτησης αποθήκευσης
      }
      setShowConfirmModal(false);
    } catch (err) {
      setAlertMessage('Η ενέργεια απέτυχε.');
      setAlertVariant('danger');
      setShowToast(true);
    }
  };

  const handleClose = () => {
    setShowModal(false);
    setEditMeal(null);
    setFormData({
      name: '',
      description: '',
      category: '',
    });
  };

  const handleShow = (meal) => {
    setEditMeal(meal);
    setShowModal(true);
    setFormData({
      id: meal?.id,
      name: meal?.name || '',
      description: meal?.description || '',
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // normalize του description
    const normalizedValue = name === 'description' ? normalizeText(value) : value;

    setFormData((prev) => ({
      ...prev,
      [name]: normalizedValue, // Χρησιμοποιούμε το κανονικοποιημένο κείμενο για το description
    }));

    handleBlur(name, normalizedValue);
  };


  const handleDelete = async (id) => {
    try {
      await dispatch(deleteMeal(id)).unwrap();
      setAlertMessage('Το γεύμα διαγράφηκε με επιτυχία.');
      setAlertVariant('success');
      setShowToast(true);
    } catch (err) {
      setAlertMessage(err);
      setAlertVariant('danger');
      setShowToast(true);
    }
  };

  const handleSave = async () => {
    // e.preventDefault();
    let newMeal = { ...formData };

    if (editMeal) {
      const { id, ...rest } = newMeal;
      newMeal = rest;
    }

    try {
      if (editMeal) {
        await dispatch(updateMeal({ mealID: editMeal?.id, mealData: newMeal })).unwrap();
        setAlertMessage(`Το γεύμα ${newMeal?.name} ενημερώθηκε με επιτυχία.`);
      } else {
        await dispatch(addMeal(newMeal)).unwrap();
        setAlertMessage(`Το γεύμα ${newMeal?.name} προστέθηκε με επιτυχία.`);
      }
      setAlertVariant('success');
      setShowToast(true);
    } catch (err) {
      setShowToast(true);
      setAlertMessage(err);
      setAlertVariant('danger');
      setShowToast(false);
    }

    handleClose();
  };

  const handleBlur = (fieldName, value) => {
    let error = '';

    if (fieldName === 'name' && value.length < 3) {
      error = 'Το όνομα πρέπει να αποτελείται από τουλάχιστον 3 χαρακτήρες.';
    }

    if (fieldName === 'description') {
      if (value.trim() === '') {
        error = 'Αν δεν υπάρχει αλλεργιογόνο, παρακαλώ εισάγετε το σύμβολο "-".';
      } else if (value.trim() === '-') {
        error = '';
      } else if (!value.includes(',')) {
        error = 'Εισάγεται τα αλλεργιογόνα χωρισμένα με κόμμα.';
      }
    }

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [fieldName]: error,
    }));
  };

  const isFormValid = () => {
    const filteredErrors = Object.values(formErrors);
    return filteredErrors.every((error) => error === '');
  };

  const handleLoadMore = () => {
    setMealCount(prevCount => prevCount + 10);
  };

  const handleShowAllMeals = () => {
    setMealCount(filteredMeals.length); // Εμφανίζει όλα τα γεύματα
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredMeals = sortedMeals.filter(meal => {
    const matchSearch = meal?.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchAllergen = selectedAllergens.length === 0 || selectedAllergens.some(allergen =>
      meal?.description && meal?.description.includes(allergen)
    );
    return matchSearch && matchAllergen;
  });

  const renderContent = () => {
    if (status === 'loading') {
      return <LoadingComponent />;
    }

    if (status === 'error') {
      return <Alert variant="danger">Σφάλμα: {error}</Alert>;
    }

    if (filteredMeals.length === 0) {
      return <Alert variant="info">Δεν βρέθηκαν γεύματα.</Alert>;
    }

    return (
      <>
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Όνομα</th>
              <th>Αλλεργιογόνα</th>
              <th>Ενέργειες</th>
            </tr>
          </thead>
          <tbody>
            {filteredMeals.slice(0, mealCount).map((meal) => (
              <tr key={meal?.id}>
                <td>{meal?.name}</td>
                <td>{meal?.description}</td>
                <td style={{ display: 'flex', justifyContent: 'center' }}>
                  <Button className='btn-edit' onClick={() => handleShow(meal)}>
                    <OverlayTrigger placement="bottom" overlay={<Tooltip className="in" id="tooltip-top">
                      Επεξεργασία πιάτου.
                    </Tooltip>
                    }>
                      <FontAwesomeIcon icon={faEdit} />
                    </OverlayTrigger>
                  </Button>
                  <Button className='btn-delete' onClick={() => handleShowConfirm('delete', meal)}>
                    <OverlayTrigger placement="bottom" overlay={<Tooltip className="in" id="tooltip-top">
                      Διαγραφή πιάτου.
                    </Tooltip>
                    }>
                    <FontAwesomeIcon icon={faTrash} />
                    </OverlayTrigger>
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        {mealCount < filteredMeals.length && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button className="btn-load" onClick={handleLoadMore}>Περισσότερα</Button>
          </div>
        )}
      </>
    );
  };

  return (
    <Container className="meal-container" style={{minHeight:'100vh'}}>
      <Card className="p-0">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div>

          <h3>Διαχείριση Γευμάτων</h3>
          <h6 style={{color:'gray'}}><strong>{'Συνολικά Γεύματα: '}</strong>{meals.length}</h6>
          </div>
          <Button className="btn-add" onClick={() => handleShow(null)}>
            <FontAwesomeIcon icon={faPlus} /> Νέο Γεύμα
          </Button>
        </Card.Header>
        <Card.Body>
          <Form.Group controlId="allergenFilters" className="mb-3">
            <Form.Label>Φίλτρο Αλλεργιογόνων</Form.Label>
            <Row>
              {/* Checkbox για κάθε αλλεργιογόνο */}
              {allergens.map((allergen, index) => (
                <Col xs={6} sm={4} md={3} lg={2} key={index} className="mb-2">
                  <Form.Check
                    type="checkbox"
                    id={`allergen-${index}`}  // Μοναδικό id για κάθε checkbox
                    label={allergen}
                    value={allergen}
                    onChange={handleAllergenChange}
                  />
                </Col>
              ))}

              {/* Checkbox για γεύματα χωρίς αλλεργιογόνα */}
              <Col xs={6} sm={4} md={3} lg={2} className="mb-2">
                <Form.Check
                  type="checkbox"
                  id="no-allergens"  // Μοναδικό id για τα γεύματα χωρίς αλλεργιογόνα
                  label="Γεύματα χωρίς αλλεργιογόνα"
                  value="no-allergens"
                  onChange={handleNoAllergensChange}
                />
              </Col>
            </Row>
          </Form.Group>
          <div style={{ display: 'flex' }}>

            <FormControl
              type="text"
              placeholder="Αναζήτηση με όνομα γεύματος"
              className="mb-3"
              value={searchTerm}
              onChange={handleSearch}
              autoComplete='off'
              autoCorrect='off'
              spellCheck='false'
            />
            <Button className="btn-show-all ms-3 mb-3" onClick={handleShowAllMeals}>
              <OverlayTrigger placement="bottom" overlay={<Tooltip className="in" id="tooltip-top">
                Προβολή όλων των γευμάτων.
              </Tooltip>
              }>
                <FontAwesomeIcon icon={faEye} />

              </OverlayTrigger>
            </Button>
          </div>
          {renderContent()}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={handleClose}>
        <Form>
          <Modal.Header closeButton>
            <Modal.Title>{editMeal ? 'Επεξεργασία Γεύματος' : 'Νέο Γεύμα'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3" controlId="formMealName">
              <Form.Label>Όνομα</Form.Label>
              <OverlayTrigger
                placement="bottom"
                show={!!formErrors['name']}
                overlay={<Tooltip>{formErrors['name']}</Tooltip>}
              >
                <Form.Control
                  type="text"
                  name="name"
                  value={formData?.name}
                  onChange={handleChange}
                  onBlur={(e) => handleBlur('name', e.target.value)}
                  required
                />
              </OverlayTrigger>
            </Form.Group>
            <Form.Group className="mb-3" controlId="formMealDescription">
              <Form.Label>Αλλεργιογόνα</Form.Label>
              <OverlayTrigger
                placement="bottom"
                show={!!formErrors['description']}
                overlay={<Tooltip>{formErrors['description']}</Tooltip>}
              >
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={formData?.description}
                  onChange={handleChange}
                  onBlur={(e) => handleBlur('description', e.target.value)}
                />
              </OverlayTrigger>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}><strong>Άκυρο</strong></Button>
            <Button variant="primary" type="button" onClick={(e) => handleShowConfirm(editMeal ? 'edit' : 'add', editMeal, e)} disabled={!isFormValid()}>
              <strong>{editMeal ? 'Ενημέρωση' : 'Προσθήκη'}</strong>
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showConfirmModal} onHide={handleConfirmClose}>
        <Modal.Header closeButton>
          <Modal.Title>Επιβεβαίωση
            {confirmAction.action === 'delete' && ' Διαγραφής'}
            {confirmAction.action === 'edit' && ' Επεξεργασίας'}
            {confirmAction.action === 'add' && ' Προσθήκης Χρήστη'} </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirmAction.action === 'delete' && (
            <p>Είστε σίγουροι ότι θέλετε να διαγράψετε το γεύμα {confirmAction.user?.first_name} {confirmAction.user?.last_name};</p>
          )}
          {confirmAction.action === 'edit' && (
            <p>Είστε σίγουροι ότι θέλετε να επεξεργαστείτε το γεύμα {confirmAction.user?.first_name} {confirmAction.user?.last_name};</p>
          )}
          {confirmAction.action === 'add' && (
            <p>Είστε σίγουροι ότι θέλετε να προσθέσετε νεό γεύμα;</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" className="modal-btn" onClick={handleConfirmClose}>
            <strong>Ακύρωση</strong>
          </Button>
          <Button variant="primary" className="modal-btn" onClick={handleConfirmAction}>
            <strong>Επιβεβαίωση</strong>
          </Button>
        </Modal.Footer>
      </Modal>

      <Notifications
        show={showToast}
        onClose={() => setShowToast(false)}
        message={alertMessage}
        variant={alertVariant}
      />
    </Container>
  );
};

export default MealManagement;
